import { calcularAforo } from '@eventos-cien/shared';
import { Callout } from '../components/Callout';
import { Card } from '../components/Card';
import { useAdminEventoData } from './AdminEventoDataProvider';

export function EventoResumenScreen() {
  const { invitaciones, mesas, evento } = useAdminEventoData();

  const objetivo = evento?.aforoObjetivo ?? 0;
  const a = calcularAforo(invitaciones, objetivo);
  const altasPuerta = invitaciones.filter((i) => i.origen === 'puerta').length;

  const porEnviar = invitaciones.filter((i) => i.estado === 'disponible' || i.estado === 'en_mano').length;
  const enviadas = invitaciones.filter((i) => i.estado === 'entregada' || i.estado === 'registrada').length;
  const registros = invitaciones.filter((i) => i.estado === 'registrada').length;

  return (
    <div className="w-full max-w-full space-y-4">
      {/* Sección Invitaciones */}
      <div className="w-full space-y-1.5">
        <h2 className="text-[11px] font-semibold uppercase tracking-wider text-texto/50">
          Invitaciones
        </h2>
        <div className="grid w-full grid-cols-3 gap-2">
          <Tarjeta n={porEnviar} etiqueta="Por enviar" />
          <Tarjeta n={enviadas} etiqueta="Enviadas" />
          <Tarjeta n={registros} etiqueta="Registros" />
        </div>
      </div>

      {/* Sección Aforo */}
      <div className="w-full space-y-1.5">
        <h2 className="text-[11px] font-semibold uppercase tracking-wider text-texto/50">Aforo</h2>
        <div className="grid w-full grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
          <Tarjeta n={objetivo} etiqueta="Aforo máximo" />
          <Tarjeta n={a.comprometido} etiqueta="Comprometido" objetivo={objetivo} tono="oro" />
          <Tarjeta n={a.confirmado} etiqueta="Confirmados" objetivo={objetivo} tono="verde" />
          <Tarjeta n={a.presentes} etiqueta="Presentes" objetivo={objetivo} tono="azul" />
          {/* col-span-2 en móvil para que no quede huerfana */}
          <div className="col-span-2 sm:col-span-1">
            <Tarjeta
              n={a.disponible}
              etiqueta="Disponible"
              objetivo={objetivo}
              tono="gris"
              alerta={a.disponible < 0}
            />
          </div>
        </div>
      </div>

      {/* Leyenda inferior compacta */}
      <div className="w-full rounded-xl bg-slate-50 p-2.5 text-xs text-texto/70">
        <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
          <span><strong>Mesas:</strong> {mesas.length}</span>
          <span>·</span>
          <span><strong>Altas en puerta:</strong> {altasPuerta}</span>
          <span>·</span>
          <span><strong>Total:</strong> {invitaciones.length}</span>
        </p>
      </div>

      {a.disponible < 0 && (
        <Callout tono="alerta">
          El aforo objetivo está excedido por {-a.disponible} personas.
        </Callout>
      )}
    </div>
  );
}

const TONOS_BARRA: Record<'oro' | 'verde' | 'azul' | 'navy' | 'gris', string> = {
  oro: 'bg-oro',
  verde: 'bg-emerald-500',
  azul: 'bg-sky-500',
  navy: 'bg-navy',
  gris: 'bg-texto/25',
};

function Tarjeta({
  n,
  etiqueta,
  alerta,
  objetivo,
  tono = 'navy',
}: {
  n: number;
  etiqueta: string;
  alerta?: boolean;
  objetivo?: number;
  tono?: 'oro' | 'verde' | 'azul' | 'navy' | 'gris';
}) {
  const proporcion = objetivo && objetivo > 0 ? Math.max(0, Math.min(1, n / objetivo)) : null;

  return (
    <Card className={['flex flex-col justify-between p-3', alerta ? 'border-red-300 bg-red-50' : ''].join(' ')}>
      <div className="min-w-0">
        <span className={['block truncate text-xl font-bold', alerta ? 'text-red-700' : 'text-texto'].join(' ')}>
          {n}
        </span>
        <span className="block truncate text-xs text-texto/60" title={etiqueta}>
          {etiqueta}
        </span>
      </div>

      {proporcion !== null && (
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-navy/10">
          <div
            className={[
              'h-full rounded-full transition-all duration-300',
              alerta ? 'bg-red-500' : TONOS_BARRA[tono],
            ].join(' ')}
            style={{ width: `${proporcion * 100}%` }}
          />
        </div>
      )}
    </Card>
  );
}
